import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Heart, Vote as VoteIcon, BarChart3, Lock, Users } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

const VOTE_TYPE_LABEL = {
  single: "Choix unique",
  multi: "Choix multiples",
  binary: "Binaire",
  approval: "Approbation",
  ranked: "Classement",
};

function isClosed(endsAt) {
  if (!endsAt) return false;
  try { return new Date(endsAt) < new Date(); } catch { return false; }
}

function FundraiserCard({ f }) {
  const pct = f.goal > 0 ? Math.min(100, Math.round((f.raised / f.goal) * 100)) : 0;
  return (
    <Link to={`/fundraiser/${f.id}`} className="z-event-card" data-testid={`fundraiser-${f.id}`}>
      {f.image_url ? (
        <img src={f.image_url} alt="" className="z-event-card-img" />
      ) : (
        <div className="z-event-card-img" style={{ background: "linear-gradient(135deg,#DA7101,#01696F)" }} />
      )}
      <div className="z-event-card-body">
        <h3 className="z-event-card-title">{f.title}</h3>
        <p className="z-event-card-meta" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {f.organizer_name}
          <VerifiedBadge verified={f.organizer_verified} variant="icon" size={13} testId={`participate-fund-verified-${f.id}`} />
        </p>
        <div className="z-progress" style={{ margin: "10px 0 6px" }}>
          <div className="z-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div style={{ fontSize: 13, color: "var(--z-text)", fontWeight: 600 }}>
          {f.raised.toFixed(0)} € <span style={{ color: "var(--z-muted)", fontWeight: 400 }}>/ {f.goal.toFixed(0)} €</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--z-muted)" }}>{f.contributors_count} contributeur(s)</div>
      </div>
    </Link>
  );
}

function VoteCard({ v }) {
  const closed = isClosed(v.ends_at);
  return (
    <Link to={`/vote/${v.id}`} className="z-card" style={{ padding: 18, display: "block" }} data-testid={`public-vote-${v.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <span className="z-chip z-chip-active" style={{ fontSize: 11 }}>{VOTE_TYPE_LABEL[v.vote_type] || v.vote_type}</span>
        {v.restricted && <span className="z-chip" style={{ fontSize: 11 }}><Lock size={11} /> Restreint</span>}
        {closed
          ? <span className="z-chip" style={{ fontSize: 11, background: "#fee", color: "var(--z-error,#c00)" }}>Clôturé</span>
          : <span className="z-chip" style={{ fontSize: 11, background: "#e6f7f8", color: "var(--z-teal)" }}>Ouvert</span>}
      </div>
      <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4, color: "var(--z-text)" }}>{v.title}</h3>
      {v.description && (
        <p style={{ fontSize: 13, color: "var(--z-muted)", marginBottom: 10, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{v.description}</p>
      )}
      <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--z-muted)", flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Users size={12} /> {v.total_votes} votant(s)</span>
        {v.organizer_name && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>· {v.organizer_name} <VerifiedBadge verified={v.organizer_verified} variant="icon" size={12} testId={`participate-vote-verified-${v.id}`} /></span>}
      </div>
    </Link>
  );
}

function SurveyCard({ s }) {
  return (
    <Link to={`/survey/${s.id}`} className="z-card" style={{ padding: 18, display: "block" }} data-testid={`public-survey-${s.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span className="z-chip z-chip-active" style={{ fontSize: 11, background: "var(--z-orange)" }}>Sondage</span>
      </div>
      <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4, color: "var(--z-text)" }}>{s.title}</h3>
      {s.description && (
        <p style={{ fontSize: 13, color: "var(--z-muted)", marginBottom: 10, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.description}</p>
      )}
      <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--z-muted)", flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><BarChart3 size={12} /> {s.responses_count} réponse(s)</span>
        <span>· {s.questions?.length || 0} question(s)</span>
        {s.organizer_name && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>· {s.organizer_name} <VerifiedBadge verified={s.organizer_verified} variant="icon" size={12} testId={`participate-survey-verified-${s.id}`} /></span>}
      </div>
    </Link>
  );
}

const VALID_TABS = ["fundraisers", "votes", "surveys"];

export default function ParticipatePage() {
  const [params, setParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(params.get("tab")) ? params.get("tab") : "fundraisers";
  const [tab, setTab] = useState(initialTab);
  const [fundraisers, setFundraisers] = useState([]);
  const [votes, setVotes] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/fundraisers").then((r) => setFundraisers(r.data)).catch(() => setFundraisers([])),
      api.get("/votes").then((r) => setVotes(r.data)).catch(() => setVotes([])),
      api.get("/surveys").then((r) => setSurveys(r.data)).catch(() => setSurveys([])),
    ]).finally(() => setLoading(false));
  }, []);

  const changeTab = (next) => {
    setTab(next);
    setParams({ tab: next }, { replace: true });
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;

  return (
    <div className="z-container" data-testid="participate-page">
      <h1 className="z-page-title">Participer</h1>
      <p className="z-page-sub">Soutenez, votez, donnez votre avis — sans compte requis.</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        <button type="button" onClick={() => changeTab("fundraisers")} className={`z-chip ${tab === "fundraisers" ? "z-chip-active" : ""}`} data-testid="participate-tab-fundraisers">
          <Heart size={14} /> Cagnottes ({fundraisers.length})
        </button>
        <button type="button" onClick={() => changeTab("votes")} className={`z-chip ${tab === "votes" ? "z-chip-active" : ""}`} data-testid="participate-tab-votes">
          <VoteIcon size={14} /> Votes ({votes.length})
        </button>
        <button type="button" onClick={() => changeTab("surveys")} className={`z-chip ${tab === "surveys" ? "z-chip-active" : ""}`} data-testid="participate-tab-surveys">
          <BarChart3 size={14} /> Sondages ({surveys.length})
        </button>
      </div>

      {tab === "fundraisers" && (
        fundraisers.length === 0 ? (
          <EmptyState icon={<Heart size={28} />} title="Aucune cagnotte pour l'instant" message="Revenez bientôt." testId="fundraisers-empty" />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 18 }}>
            {fundraisers.map((f) => <FundraiserCard key={f.id} f={f} />)}
          </div>
        )
      )}

      {tab === "votes" && (
        votes.length === 0 ? (
          <EmptyState icon={<VoteIcon size={28} />} title="Aucun vote ouvert" message="Revenez bientôt pour participer." testId="public-votes-empty" />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
            {votes.map((v) => <VoteCard key={v.id} v={v} />)}
          </div>
        )
      )}

      {tab === "surveys" && (
        surveys.length === 0 ? (
          <EmptyState icon={<BarChart3 size={28} />} title="Aucun sondage pour l'instant" message="Revenez bientôt." testId="public-surveys-empty" />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
            {surveys.map((s) => <SurveyCard key={s.id} s={s} />)}
          </div>
        )
      )}
    </div>
  );
}
